import * as React from 'react'

export interface LegalSection {
  number: string
  title: string
  body: React.ReactNode
}

export const TERMS_SECTIONS: LegalSection[] = [
  {
    number: '1',
    title: 'Services Description',
    body: (
      <p>
        The Services provide a platform (the &ldquo;FuelApp Platform&rdquo;) that includes tools for managing
        financial aspects of trucking operations including but not limited to accessing fuel discounts,
        managing fleet spend, and compiling tax-related spend data. The Services also enable Customer to
        purchase motor fuel and related products at participating third-party fuel stations (&ldquo;Partner
        Stations&rdquo;) by means of single-use fuel codes issued through the FuelApp Platform, as described
        in Sections 4.1 through 4.3. Fuel is dispensed by the Partner Station and not by us, and fuel
        transactions are reported and settled through one or more third-party fuel network partners.
      </p>
    ),
  },
  {
    number: '2',
    title: 'General Provisions',
    body: (
      <>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.1 Accounts</h3>
          <p>
            If you are registering an Account as an administrator of the Services on behalf of a Customer
            entity, (a) you represent and warrant that you have the authority to legally bind the entity and
            to grant us all permissions and licenses provided in this Agreement; (b) the words
            &ldquo;you&rdquo; and &ldquo;your&rdquo; used in this Agreement mean Customer, i.e., the entity
            registering the Account, in addition to each individual authorized by the entity to access the
            Services (an &ldquo;Authorized User&rdquo;); (c) you are responsible for administering
            Customer&rsquo;s access to the Services by adding and removing Authorized Users and keeping all
            Access Codes secure and confidential. You agree to notify us immediately of any unauthorized use
            of your Account and are responsible for anything that happens through your Account prior to
            closing it or reporting misuse to us. Customer is responsible for Authorized Users&rsquo;
            compliance with this Agreement, including for ensuring that Authorized Users maintain the
            confidentiality of Access Codes and user names.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.2 Customer Content</h3>
          <p>
            Authorized Users may submit content or information to the Services, which includes Personal
            Information and User Content (collectively, &ldquo;Customer Content&rdquo;), and Customer will
            have the sole right and responsibility for managing your use of it. Customer will (a) inform
            Authorized Users of all Customer policies and practices that are relevant to their use of the
            Services and of any settings that may impact the processing of Customer Content; and (b) obtain
            all rights, permissions and/or consents from Authorized Users and other Customer personnel that
            are necessary to grant the rights and licenses in the Agreement and for the lawful use and
            transmission of Customer Content and the operation of the Services.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.3 Subscriptions</h3>
          <p>
            A subscription allows Customer and its Authorized Users to access the Services. A subscription may
            be procured through the Services interface, or in some cases, via an order form entered into
            between Customer and us (each, an &ldquo;Order Form&rdquo;). Subscriptions commence when we make
            the Services available to Customer and continue for the term specified in the Services or in the
            Order Form, as applicable, or as earlier terminated as described below (the &ldquo;Initial
            Subscription Period&rdquo;). A &ldquo;Renewal Subscription Period&rdquo; means a period of time
            equivalent to one (1) year or the Initial Subscription Period, whichever is shorter. Please read
            the &ldquo;Auto-Renewals&rdquo; Section 5.2 below to learn more about automatic renewals.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.4 Feedback</h3>
          <p>
            Customer and its Authorized Users may choose to, or we may invite them to, submit comments or
            ideas about the Services (&ldquo;Ideas&rdquo;). By submitting any Idea, Customer agrees that its
            disclosure is gratuitous, unsolicited and without restriction and will not place us under any
            fiduciary or other obligation, and that we are free to use the Idea without any compensation to
            Customer, the Authorized User, or anyone else, and/or to disclose the Idea on a non-confidential
            basis or otherwise to anyone.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            2.5 Privacy Policy and Protection of Personal Information
          </h3>
          <p>
            Please review our Privacy Policy for more information on how we collect and use data relating to
            the use and performance of our websites, services, and products, including personal information
            about our Customers and Authorized Users. We will process Personal Information in accordance with
            the terms of the Privacy Policy and will not process Personal Information for other purposes
            without your prior written instruction.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.6 Location Data</h3>
          <p>
            To provide the Services, we may collect location data from you and your Authorized Users,
            including specific information such as your or your Authorized Users&rsquo; IP addresses, and
            latitude/longitude coordinates (including but not limited to GPS/GNSS). Customer acknowledges and
            agrees that we may use and retain this data to provide and improve our products and services,
            subject to our Privacy Policy.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.7 Notifications and Communications</h3>
          <p>
            We will communicate with you via fax, phone call, text (SMS) message, email or push notifications
            in order that we may provide the Services, service your Account, reasonably address matters
            pertaining to your Account or for other purposes reasonably related to the Services and our
            business, including but not limited to notifying you about upcoming loads and sending you
            marketing-related messages.
          </p>
          <p>
            <strong>Calls and SMS text messages.</strong> By creating an Account or using the Services you
            expressly consent and agree to accept and receive communications from us, including via fax, text
            (SMS) messages and phone calls at any of your landline or mobile phone numbers you provided, even
            if you are listed on any federal, state, provincial or other applicable &ldquo;Do Not Call&rdquo;
            list, in order that we may provide the Services, including but not limited to operational
            communications concerning your Account or use of the Services, information concerning new and
            existing loads and features on the Services, and other Services-related notifications and
            communications. With your consent, we may also send you promotional calls or text messages
            containing news and special offers, discounts or other promotional or commercial content
            (&ldquo;Promotional Calls&rdquo;). You understand and expressly authorize us to send you these
            communications, including live calls from an operator, or pre-recorded or auto-generated voice
            messages, or SMS text messages, and that we may use automated telephone dialing systems. Standard
            rates and charges for all calls or texts, as applied by your cell phone carrier, will apply. You
            represent that you have received, and are authorized to convey to us, the consent of any
            Authorized Users on your Account to be contacted by us as described in this section. You agree
            that all consents provided in this section will survive cancellation of your Account. You may
            opt-out by replying STOP to any text message from us, or by emailing{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a>. Note that opting out of all text messages
            will suspend your use of the Services until you contact us at{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a> to reinstate Access Codes.
          </p>
          <p>
            <strong>Email communications.</strong> We may send you confirmation and other transactional emails
            regarding the Services. We may also send you emails about other services that we think might
            interest you (&ldquo;Promotional Emails&rdquo;). You can unsubscribe from Promotional Emails at
            any time by clicking unsubscribe in our email communications or contacting us at{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a>.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">2.8 Free Trial Account and Services</h3>
          <p>
            We may, from time to time, offer a free trial, promotional offer, or other type of limited offer
            for use of all or part of our FuelApp Platform (a &ldquo;Free Trial&rdquo;). The duration of the
            Free Trial will be specified at sign-up. If you register for a Free Trial, you may be presented
            with additional terms and conditions when registering your account, and any such additional terms
            and conditions are hereby incorporated into these Customer Terms by reference and are legally
            binding. The Free Trial may not include access to all of the features or functions of the FuelApp
            Platform or our Services. Any data that you enter into the Free Trial may be permanently lost at
            the end of the trial period unless you (a) purchase a subscription plan for the Services, or (b)
            export such data before the end of the trial period.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '3',
    title: 'Services Usage and Restrictions',
    body: (
      <>
        <p>
          We own and will continue to own our Services and Documentation, including all related intellectual
          property and other proprietary rights related thereto. We may collect data relating to
          Customer&rsquo;s and its Authorized Users&rsquo; usage of the Services (&ldquo;Usage Data&rdquo;)
          and collect, analyze, and use data derived from User Content that has been aggregated and/or
          anonymized such that it does not identify Customer or any identifiable individual person
          (&ldquo;Derivative Data&rdquo; and, collectively with Usage Data, &ldquo;Company Data&rdquo;). All
          Company Data will be owned solely and exclusively by us.
        </p>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            3.1.2 Licenses to the Services and Documentation
          </h3>
          <p>
            During the Subscription Period, we grant the Customer a non-exclusive, non-transferable license to
            access and use, and to permit Authorized Users to access and use the Services, in accordance with
            the Agreement, for the Customer&rsquo;s own internal business purposes.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.2 Customer&rsquo;s Licenses to Us</h3>
          <p>
            As between us on the one hand, and Customer and any Authorized Users on the other, Customer will
            own all Customer Content, including Personal Information and User Content.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.2.2 License to Personal Information</h3>
          <p>
            Subject to the terms and conditions of the Agreement, Customer grants us a worldwide,
            non-exclusive, limited term license to access, use, process, copy, distribute, perform, export and
            display Personal Information, only as reasonably necessary (i) to provide and maintain the
            Services; (ii) to prevent or address service, security, support or technical issues; (iii) as
            required by law; and (iv) as expressly permitted in writing by Customer.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.2.3 License to User Content</h3>
          <p>
            With respect to that portion of Customer Content that consists of videos, images, music, comments,
            questions, documents, spreadsheets, business terms, information, and any other content submitted,
            posted, or otherwise made available by Customer and its Authorized Users through the Services
            (&ldquo;User Content&rdquo;), Customer grants to us a royalty-free, sublicensable, transferable,
            perpetual, irrevocable, non-exclusive, worldwide license to use, host, store, reproduce, modify,
            publish, translate, distribute, publicly perform, publicly display, and make derivative works of
            all such User Content, solely for use in connection with our provision of the Services.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.3 Responsibilities for Customer Content</h3>
          <p>
            We are not responsible for the content of any Customer Content or the way Customer or its
            Authorized Users choose to use the Services to store or process any Customer Content. Customer
            represents and agrees that Customer is solely responsible for (a) providing notices and obtaining
            consents as legally required from its Authorized Users and any third parties for the collection,
            use, processing and transfer of Customer Content in connection with the Services; and (b) ensuring
            compliance with all applicable laws in all jurisdictions relating to data privacy and security.
            Unless otherwise agreed to in writing, Customer may not submit any Customer Content that includes
            a social security number, passport number, driver&rsquo;s license number, credit card or debit
            card number, or any other information subject to specific data privacy and security laws
            including, but not limited to, GLBA, HIPAA, HiTECH, FERPA, COPPA, or the GDPR.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.4 Use of the Services</h3>
          <p>
            Customer must comply with the Agreement and ensure that its Authorized Users comply with the
            Agreement. We may review conduct for compliance purposes, but we have no obligation to do so
            unless required by law. We reserve the right to take appropriate action, including asking Customer
            to remove certain Customer Content, when we deem it reasonably appropriate.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.5 Acceptable Use</h3>
          <p>You agree not to engage in any of the following prohibited activities:</p>
          <ul className="m-0 flex flex-col gap-2 pl-6">
            <li>
              copying, distributing, or disclosing any part of the Services in any medium, including by any
              automated &ldquo;scraping&rdquo;;
            </li>
            <li>
              using any automated system to access the Services in a manner that sends excessive request
              messages to our servers;
            </li>
            <li>transmitting spam, chain letters, or other unsolicited email;</li>
            <li>
              attempting to interfere with, compromise the system integrity or security or decipher any
              transmissions to or from the servers running the Services;
            </li>
            <li>
              taking any action that imposes, or may impose an unreasonable or disproportionately large load
              on our infrastructure;
            </li>
            <li>uploading invalid data, viruses, worms, or other software agents through the Services;</li>
            <li>
              collecting or harvesting any personally identifiable information, including Account names, from
              the Services;
            </li>
            <li>using the Services for any commercial solicitation purposes;</li>
            <li>
              impersonating another person or entity, conducting fraud, hiding or attempting to hide your
              identity;
            </li>
            <li>interfering with the proper working of the Services;</li>
            <li>
              accessing any content on the Services through any technology or means other than those provided
              or authorized by the Services; or
            </li>
            <li>bypassing the measures we may use to prevent or restrict access to the Services.</li>
          </ul>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">3.5.2 Customer Content Restrictions</h3>
          <p>
            You are solely responsible for the content of any Customer Content you submit through the
            Services. You agree not to submit any Customer Content that: (i) may create a risk of harm or
            injury to any person or animal; (ii) may create a risk of any loss or damage to any person or
            property; (iii) seeks to harm or exploit children; (iv) may constitute or contribute to a crime or
            tort; (v) contains unlawful, harmful, abusive, defamatory, harassing, threatening, profane, or
            otherwise objectionable content; (vi) contains illegal information; (vii) contains any information
            or content that you do not have a right to make available; or (viii) contains any information or
            content that you know is not correct and/or current.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '4',
    title: 'Payment Obligations',
    body: (
      <>
        <p>
          This Section 4 applies to fees charged for access to the Services. Customer&rsquo;s purchases of
          fuel through the Services are governed by Sections 4.1, 4.2 and 4.3. Any fees that we may charge for
          stand-alone access to the Services will be specified through the Services interface or in the Order
          Form(s) and must be paid in advance. Payment obligations are non-cancelable and, except as expressly
          stated in the Agreement, fees paid are non-refundable. If we agree to invoice Customer by email,
          full payment must be received within thirty (30) days from the invoice date. Fees are stated
          exclusive of any taxes, levies, duties, or similar governmental assessments. Customer will be
          responsible for paying all Taxes associated with its purchases, except for those taxes based on our
          net income.
        </p>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.1.2 How Fuel Purchases Work</h3>
          <p>
            When Customer selects a Partner Station in the FuelApp Platform and requests a fuel code, Customer
            is making a purchase of fuel at that Partner Station. Each fuel code is single-use, is valid only
            at the Partner Station selected, and expires thirty (30) minutes after it is issued. We issue a
            fuel code only after an authorization on Customer&rsquo;s payment method has been approved.
            Customer enters the fuel code at the Partner Station&rsquo;s pump or fuel desk, and the Partner
            Station&rsquo;s equipment meters the fuel dispensed.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.1.3 When Delivery Occurs</h3>
          <p>
            Delivery of fuel is complete, and fuel is deemed received by Customer, at the time fuel is
            dispensed at the Partner Station against a fuel code issued to Customer&rsquo;s account, as
            reported to us by our fuel network partner. Delivery is not conditioned on, and does not occur at,
            the time any charge is authorized, presented, settled or posted to Customer&rsquo;s payment
            method.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.1.4 Responsibility for Fuel Codes</h3>
          <p>
            Customer is responsible for all fuel dispensed against a fuel code issued to Customer&rsquo;s
            account, including fuel dispensed by any person to whom Customer provides, displays or otherwise
            makes available the code. Fuel codes may not be sold, shared, transferred or assigned.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            4.2 Fuel Pricing, Authorization Holds, and Charges
          </h3>
          <p>
            The per-gallon price shown in the FuelApp Platform for a Partner Station is an estimate based on
            the most recent pricing information available to us from our fuel network partners and the Partner
            Station. Fuel prices change frequently and the pricing information available to us may not be
            current at the time Customer requests a fuel code or dispenses fuel. We do not guarantee that the
            price displayed in the FuelApp Platform will be the price at which fuel is dispensed or billed,
            and requesting a fuel code does not lock in any price. The price applied to Customer&rsquo;s
            purchase is determined at the time fuel is dispensed and is stated on the receipt made available
            to Customer in the FuelApp Platform. The price displayed on the Partner Station&rsquo;s pump or
            signage is the Partner Station&rsquo;s retail price and will not reflect any discount available
            through the Services.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.2.2 Authorization Hold</h3>
          <p>
            Before issuing a fuel code, we place a temporary authorization hold on Customer&rsquo;s payment
            method in an amount selected by Customer, up to the maximum we permit for the transaction. The
            hold is not a charge. If Customer cancels the fuel code, or the code expires before fuel is
            dispensed, the hold is released, which may take up to three (3) business days depending on
            Customer&rsquo;s card issuer.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.2.3 Final Charge; More Than One Charge</h3>
          <p>
            After fuel is dispensed, we charge Customer for the fuel actually dispensed, at the price
            determined under Section 4.2. If the amount of fuel dispensed exceeds the authorization hold, the
            purchase may settle as more than one charge on Customer&rsquo;s statement; together, those charges
            constitute a single fuel purchase. Any unused portion of the hold is released.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            4.2.4 Delayed Reporting and Continuing Authorization to Charge
          </h3>
          <p>
            Fuel transactions are reported to us by our fuel network partners after fuel is dispensed.
            Reporting is sometimes delayed, and a transaction may be reported to us days or weeks after the
            date Customer fueled. Customer authorizes us to charge Customer&rsquo;s payment method for fuel
            dispensed against a fuel code issued to Customer&rsquo;s account when the transaction is reported
            to us, whether or not that occurs on the date Customer fueled and whether or not any authorization
            hold remains in effect. Where an authorization hold has expired or been released, Customer
            authorizes us to initiate a new charge to the payment method stored on Customer&rsquo;s account
            for the amount of the fuel dispensed. The transaction date shown on Customer&rsquo;s card
            statement may therefore differ from the date Customer fueled, and the charge may appear on a later
            statement. We will not initiate a charge more than 90 days after the date fuel was dispensed.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.2.5 Receipts</h3>
          <p>
            We make an itemized receipt available to Customer in the FuelApp Platform for each fuel purchase,
            showing the Partner Station, the products and quantities dispensed, the price per unit applied and
            the total charged.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.3 Billing Questions and Payment Disputes</h3>
          <p>
            If Customer believes a charge is incorrect, Customer agrees to contact us at{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a> within sixty (60) days of the charge, and
            to allow us fifteen (15) business days to investigate, before initiating a dispute or chargeback
            with Customer&rsquo;s card issuer. We will provide transaction records for any fuel purchase,
            including the quantity dispensed and the Partner Station&rsquo;s transaction identifiers, on
            request.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.3.2 Transaction Records</h3>
          <p>
            Customer authorizes us to provide Customer&rsquo;s card issuer, our payment processor and our fuel
            network partners with records relating to Customer&rsquo;s fuel purchases, including transaction,
            device and location data recorded by the FuelApp Platform, in connection with any billing inquiry,
            payment dispute or chargeback.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.4 Billing Policies</h3>
          <p>
            We may add new services that carry additional fees and charges, or add or amend fees and charges
            for existing services, at any time in our sole discretion, by giving Customer at least 60
            days&rsquo; written notice, such increase to take effect at the beginning of the next billing
            cycle. If the Customer is unhappy with the increase, the Customer may give notice to terminate the
            Agreement by giving at least 30 days&rsquo; written notice, such notice to expire no later than
            the end of the then-current billing cycle.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">4.5 Payment Information</h3>
          <p>
            We may use a third-party payment processor (&ldquo;Payment Processor&rdquo;) to process payments
            you make in connection with the Services. Payment must be received by Payment Processor before our
            acceptance of an order. We do not view or store your full credit card or other payment method
            information. You acknowledge and agree that we are not responsible for any security or privacy
            breaches of your credit card or other payment method.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '5',
    title: 'Term and Termination',
    body: (
      <>
        <p>
          A free subscription continues until terminated, while a paid subscription has a Subscription Period
          that may expire or be terminated. The Agreement remains effective until all subscriptions ordered
          under the Agreement have expired or been terminated, or the Agreement itself terminates. Termination
          of the Agreement will terminate all subscriptions and all Order Forms.
        </p>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">5.2 Auto-Renewal</h3>
          <p className="text-ink-500 text-sm">
            UNLESS OTHERWISE SET FORTH IN AN ORDER FORM, (A) THE INITIAL SUBSCRIPTION PERIOD AND ANY RENEWAL
            SUBSCRIPTION PERIODS RENEW FOR ADDITIONAL RENEWAL SUBSCRIPTION PERIODS; AND (B) THE PER-UNIT
            PRICING DURING ANY AUTOMATIC RENEWAL SUBSCRIPTION PERIOD WILL BE THE THEN-CURRENT PRICE
            COMMUNICATED TO YOU BY US. EITHER PARTY CAN GIVE THE OTHER NOTICE OF NON-RENEWAL AT LEAST THIRTY
            (30) DAYS BEFORE THE END OF THE INITIAL SUBSCRIPTION PERIOD OR THE THEN-CURRENT RENEWAL
            SUBSCRIPTION PERIOD TO STOP A SUBSCRIPTION FROM AUTOMATICALLY RENEWING.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">5.3 Termination for Cause</h3>
          <p>
            We or Customer may terminate the Agreement on notice to the other party if the other party
            materially breaches the Agreement and such breach is not cured within thirty (30) days after the
            non-breaching party provides notice of the breach. We may terminate the Agreement immediately on
            notice to Customer if we reasonably believe that the Services are being used in violation of
            applicable law.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">5.4 Termination Without Cause</h3>
          <p>
            Customer may terminate its free subscriptions immediately without cause. We may terminate the
            Agreement without cause, but we will provide Customer with thirty (30) days prior written notice
            and refund Customer any prepaid fees covering the remainder of the Subscription Period after the
            effective date of termination.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">5.5 Effect of Termination</h3>
          <p>
            Upon any termination for cause by Customer, we will refund Customer any prepaid fees covering the
            remainder of the Subscription Period after the effective date of termination. Upon any termination
            for cause by us, Customer will pay any unpaid fees covering the remainder of the Subscription
            Period after the effective date of termination. In no event will any termination relieve Customer
            of the obligation to pay any fees payable to us for the period prior to the effective date of
            termination.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '6',
    title: 'Representations; Disclaimer of Warranties',
    body: (
      <>
        <p>
          Customer represents and warrants that it has validly entered into the Agreement and has the legal
          power to do so. Customer further represents and warrants that it is responsible for the conduct of
          its Authorized Users and their compliance with the terms of the Agreement.
        </p>
        <p className="text-ink-500 text-sm">
          EXCEPT AS EXPRESSLY PROVIDED FOR HEREIN, THE SERVICES AND ALL RELATED COMPONENTS AND INFORMATION ARE
          PROVIDED ON AN &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; BASIS WITHOUT ANY WARRANTIES OF
          ANY KIND, AND WE EXPRESSLY DISCLAIM ANY AND ALL WARRANTIES, WHETHER EXPRESS OR IMPLIED, INCLUDING
          THE IMPLIED WARRANTIES OF MERCHANTABILITY, TITLE, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT. CUSTOMER ACKNOWLEDGES THAT WE DO NOT WARRANT THAT THE SERVICES WILL BE
          UNINTERRUPTED, TIMELY, SECURE, OR ERROR-FREE. SOME JURISDICTIONS DO NOT ALLOW THE DISCLAIMER OF
          CERTAIN TYPES OF WARRANTIES, SO THE ABOVE DISCLAIMERS MAY NOT APPLY TO YOU.
        </p>
      </>
    ),
  },
  {
    number: '7',
    title: 'Limitation of Liability',
    body: (
      <p className="text-ink-500 text-sm">
        IN NO EVENT WILL OUR AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THE AGREEMENT (WHETHER IN
        CONTRACT OR TORT OR UNDER ANY OTHER THEORY OF LIABILITY) EXCEED THE TOTAL AMOUNT PAID BY CUSTOMER
        HEREUNDER IN THE TWELVE (12) MONTHS PRECEDING THE LAST EVENT GIVING RISE TO LIABILITY. THE FOREGOING
        WILL NOT LIMIT CUSTOMER&rsquo;S PAYMENT OBLIGATIONS UNDER THE PAYMENT TERMS SECTION ABOVE.
      </p>
    ),
  },
  {
    number: '8',
    title: 'Indemnification',
    body: (
      <>
        <p>
          Customer agrees to defend, indemnify and hold harmless us and our affiliates, licensors, and
          suppliers, and our and their respective employees, contractors, agents, officers and directors, from
          and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and
          expenses (including but not limited to attorney&rsquo;s fees) arising from:
        </p>
        <ul className="m-0 flex flex-col gap-2 pl-6">
          <li>
            Customer&rsquo;s and any Authorized User&rsquo;s use of and access to the Services, including any
            Customer Content or other content transmitted or received by Customer or any Authorized User;
          </li>
          <li>
            your violation of any term of the Agreement, including without limitation any breach of
            Customer&rsquo;s representations and warranties above;
          </li>
          <li>
            Customer&rsquo;s or any Authorized User&rsquo;s violation of any third-party right, including
            without limitation any right of privacy or intellectual property rights;
          </li>
          <li>
            Customer&rsquo;s or any Authorized User&rsquo;s violation of any applicable law, rule or
            regulation;
          </li>
          <li>
            Customer Content or any content that is submitted via Customer&rsquo;s or any Authorized
            User&rsquo;s Account, including without limitation misleading, false, or inaccurate information;
          </li>
          <li>
            Customer&rsquo;s or any Authorized User&rsquo;s gross negligence, fraud, or willful misconduct; or
          </li>
          <li>
            any other party&rsquo;s access and use of the Services with Customer&rsquo;s or any Authorized
            User&rsquo;s unique username, Access Code or other appropriate security code (provided that such
            access and use was not our fault).
          </li>
        </ul>
      </>
    ),
  },
  {
    number: '9',
    title: 'Confidentiality',
    body: (
      <>
        <p>
          Each party (&ldquo;Disclosing Party&rdquo;) may disclose &ldquo;Confidential Information&rdquo; to
          the other party (&ldquo;Receiving Party&rdquo;) in connection with the Agreement, which is anything
          that reasonably should be understood to be confidential given the nature of the information and the
          circumstances of disclosure including all Order Forms, as well as non-public business, product,
          technology and marketing information. Confidential Information of Customer includes Customer
          Content. Confidential Information does not include information that (a) is or becomes generally
          available to the public without breach of any obligation owed to the Disclosing Party; (b) was known
          to the Receiving Party prior to its disclosure by the Disclosing Party without breach of any
          obligation; (c) is received from a third party without breach of any obligation; or (d) was
          independently developed by the Receiving Party.
        </p>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            9.2 Protection and Use of Confidential Information
          </h3>
          <p>
            The Receiving Party will (a) take at least reasonable measures to prevent the unauthorized
            disclosure or use of Confidential Information, and limit access to those employees, affiliates and
            contractors who need to know such information in connection with the Agreement; and (b) not use or
            disclose any Confidential Information of the Disclosing Party for any purpose outside the scope of
            the Agreement.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">9.3 Compelled Access or Disclosure</h3>
          <p>
            The Receiving Party may access or disclose Confidential Information of the Disclosing Party if it
            is required by law; provided, however, that the Receiving Party gives the Disclosing Party prior
            notice of the compelled access or disclosure (to the extent legally permitted) and reasonable
            assistance, at the Disclosing Party&rsquo;s cost, if the Disclosing Party wishes to contest the
            access or disclosure.
          </p>
        </div>
      </>
    ),
  },
  {
    number: '10',
    title: 'Miscellaneous',
    body: (
      <>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.1 Marks License</h3>
          <p>
            Customer hereby grants to us a nonexclusive license to use, reproduce, and display
            Customer&rsquo;s name, logo, service marks, trademarks, and related brand features (&ldquo;Marks
            &rdquo;) on or within the user interface of the Services and in marketing and promotional
            materials. We will use such Marks in compliance with any reasonable written usage guidelines
            provided to us by Customer.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            10.2 Third Party Products, Links, and Information
          </h3>
          <p>
            The Services may integrate with, or contain, third-party products, services, materials, or
            information, or links thereto that are not owned or controlled by us (&ldquo;Third-Party
            Materials&rdquo;). We do not endorse or assume any responsibility for any such Third-Party
            Materials. If Customer or any Authorized User accesses any third-party website or service, it does
            so at its own risk, and the Agreement and our Privacy Policy do not apply to such use.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.3 Stripe Program</h3>
          <p>
            We have entered into an agreement with Stripe, Inc. (&ldquo;Stripe&rdquo;) to receive certain
            branded payment card tools, payment processing, and other business services. We may make those
            third party services, and the physical payment card through which they are offered, available to
            you (the &ldquo;CT Cash Card&rdquo; or &ldquo;CT Credit Card&rdquo;). Access to and use of CT Cash
            Card or Credit Card is subject to the Celtic Bank Authorized User Terms and Celtic Bank Privacy
            Policy, as well as any separate agreements in place between the parties, all of which are
            incorporated by reference into this Agreement.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.4 Force Majeure</h3>
          <p>
            Neither us nor Customer will be liable by reason of any failure or delay in the performance of its
            obligations on account of events beyond the reasonable control of a party, including
            denial-of-service attacks, a failure by a third-party hosting provider or utility provider, fires,
            floods, earthquakes, accidents, war, revolution, riots, acts of God, or acts of government;
            provided such failure could not have been prevented by reasonable precautions. In the event such
            force majeure event continues for longer than thirty (30) calendar days, the other party shall be
            entitled to terminate the Agreement without penalty or early termination fees.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            10.5 Relationship of the Parties; No Third-Party Beneficiaries
          </h3>
          <p>
            The parties are independent contractors. The Agreement does not create a partnership, franchise,
            joint venture, agency, fiduciary, or employment relationship between the parties. There are no
            third-party beneficiaries to the Agreement (subject to Section 10.14 regarding mobile
            applications).
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.6 Communications</h3>
          <p>
            Except as otherwise set forth herein, all notices under the Agreement will be by email or SMS text
            message, although we may instead choose to provide notice to Customer through the Services.
            Notices to us must be sent to <a href="mailto:support@fuel.app">support@fuel.app</a>.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.7 Modifications</h3>
          <p>
            We may change these Customer Terms and the other components of the Agreement in accordance with
            this Section. If we make a material change to the Agreement, we will provide Customer with
            reasonable notice prior to the change taking effect, either by emailing the email address
            associated with Customer&rsquo;s Account or by messaging Customer through the Services. The
            materially revised Agreement will become effective on the date set forth in our notice.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.8 Waivers</h3>
          <p>
            No failure or delay by either party in exercising any right under the Agreement will constitute a
            waiver of that right. No waiver under the Agreement will be effective unless made in writing and
            signed by an authorized representative of the party being deemed to have granted the waiver.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.9 Severability</h3>
          <p>
            The Agreement will be enforced to the fullest extent permitted under applicable law. If any
            provision of the Agreement is held by a court of competent jurisdiction to be contrary to law, the
            provision will be modified by the court and interpreted so as best to accomplish the objectives of
            the original provision to the fullest extent permitted by law, and the remaining provisions of the
            Agreement will remain in effect.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.10 Assignment</h3>
          <p>
            Neither party may assign or delegate any of its rights or obligations hereunder, whether by
            operation of law or otherwise, without the prior written consent of the other party (not to be
            unreasonably withheld). Notwithstanding the foregoing, we may assign the Agreement in its
            entirety, without consent of Customer, to a corporate affiliate or in connection with a merger,
            acquisition, corporate reorganization, or sale of all or substantially all of our assets.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.11 Governing Law</h3>
          <p>
            The Agreement, and any disputes arising out of or related hereto, will be governed exclusively by
            the internal laws of the State of California, without regard to its conflicts of laws rules or the
            United Nations Convention on the International Sale of Goods. Any arbitration conducted hereunder
            shall be governed by the Federal Arbitration Act (9 U.S.C. §§ 1-16).
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.12 Venue; Waiver of Jury Trial; Fees</h3>
          <p>
            The state and federal courts located in San Francisco County, California will have exclusive
            jurisdiction to adjudicate any dispute arising out of or relating to the Agreement. Each party
            hereby consents and submits to the exclusive jurisdiction of such courts. Each party also hereby
            waives any right to jury trial in connection with any action or litigation in any way arising out
            of or related to the Agreement. In any action or proceeding to enforce rights under the Agreement,
            the prevailing party will be entitled to recover its reasonable costs and attorneys&rsquo; fees.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.13 Arbitration</h3>
          <p>
            For any dispute with us specific to the Services, you agree to first contact us at{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a> and attempt to resolve the dispute
            informally. If we have not been able to resolve a dispute within sixty (60) days of your first
            contact, we each agree, to the fullest extent permitted by applicable law, to resolve any claim,
            dispute, or controversy (excluding any claims for injunctive or other equitable relief) arising
            out of or in connection with or relating to the Agreement by binding arbitration by JAMS, under
            the Optional Expedited Arbitration Procedures then in effect, except as provided herein. The
            arbitration will be conducted in San Francisco County, California, remotely, or at another
            mutually agreeable location. We each agree that claims shall be resolved on an individual basis
            only, and not on a class, collective, or representative basis (&ldquo;Class Waiver&rdquo;). By
            using Cai Labs&rsquo; Services, you acknowledge and agree that you have read this arbitration
            agreement carefully, are bound by it, and are WAIVING ANY RIGHT TO HAVE A TRIAL BEFORE A COURT OR
            JURY of any and all claims subject to arbitration under this agreement.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.14 Entire Agreement</h3>
          <p>
            The Agreement, including these Customer Terms and all referenced pages and Order Forms,
            constitutes the entire agreement between the parties and supersedes all prior and contemporaneous
            agreements, proposals or representations, written or oral, concerning its subject matter. To the
            extent of any conflict or inconsistency, the following order of precedence will apply: (1) the
            terms of the Lease Agreement (if any); (2) the terms of the applicable Order Form (if any); (3)
            these Customer Terms; and (4) any other documents or pages referenced in these Customer Terms.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">
            10.15 Additional Terms for Mobile Applications
          </h3>
          <p>
            The following applies to any Services acquired from the Apple App Store (&ldquo;Apple-Sourced
            Software&rdquo;): Customer acknowledges and agrees that the Agreement is solely between Customer
            and us, not Apple, Inc. (&ldquo;Apple&rdquo;) and that Apple has no responsibility for the
            Apple-Sourced Software or content thereof. Apple has no obligation whatsoever to furnish any
            maintenance and support services with respect to the Apple-Sourced Software. Customer and we
            acknowledge and agree that Apple, and Apple&rsquo;s subsidiaries, are third-party beneficiaries of
            the Agreement as relates to your license of the Apple-Sourced Software.
          </p>
          <p>
            The following applies to any Services acquired from the Google Play Store (&ldquo;Google-Sourced
            Software&rdquo;): (i) Customer acknowledges that the Agreement is between Customer and us only,
            and not with Google, Inc. (&ldquo;Google&rdquo;); (ii) Customer&rsquo;s use of Google-Sourced
            Software must comply with Google&rsquo;s then-current Google Play Store Terms of Service; (iii)
            Google is only a provider of the Google Play Store where Customer obtained the Google-Sourced
            Software; (iv) we, and not Google, are solely responsible for our Google-Sourced Software; and (v)
            Google has no obligation or liability to Customer with respect to Google-Sourced Software or the
            Agreement.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.16 Survival</h3>
          <p>
            Any section of the Agreement that, by its terms or its nature, should survive the termination or
            expiration of the Agreement shall so survive, including but not limited to Sections 2.4, 2.5, 3.2,
            3.3, 4, 5.5, and 6 through 10.
          </p>
        </div>
        <div className="flex flex-col gap-2 [&_p+p]:mt-4">
          <h3 className="text-md text-ink m-0 font-semibold">10.17 Contacting Us</h3>
          <p>
            Please feel free to contact us if you have any questions about the Customer Terms or any other
            part of the Agreement. You may contact us at{' '}
            <a href="mailto:support@fuel.app">support@fuel.app</a>.
          </p>
        </div>
      </>
    ),
  },
]
